import {
  Activity, ArrowLeftRight, Boxes, CalendarCheck, CalendarClock, ClipboardList, Database, FileWarning, Fuel, Hotel,
  Keyboard, LayoutDashboard, Link2, Map, ReceiptText, Route, Tags, Ticket, Tractor, TriangleAlert, Truck, Wheat,
} from 'lucide-react'
import type { NavSection } from '../../core/router/navConfig'

/** Navegação da área Logística: a operação, a jornada e os cadastros.
 *
 *  Até 24/10/2026 era o menu inteiro do backoffice, com a seção Geral levando
 *  Cadastros, Categorias e Auditoria. A seção Geral virou a área Administrativo, e
 *  em 26/10/2026 os cadastros gerais e as Categorias voltaram para cá (Samuel):
 *  o Administrativo ficou com Usuários, Cargos e Auditoria.
 *
 *  Desde 04/09/2026 (Samuel) o subgrupo Conjuntos saiu: Conjuntos é um item de
 *  Operação, a tela de três abas; Duplas foi para o hub Cadastros; e Transferências
 *  foi para a seção Jornada. Ícones Lucide. */
export const logisticaNav: NavSection[] = [
  {
    title: 'Operação',
    titleKey: 'nav.section.operation',
    items: [
      { label: 'Dashboard', labelKey: 'nav.dashboard', path: '/logistica/dashboard', icon: LayoutDashboard, ready: true, permission: 'dashboard.view' },
      // Pendências saiu de dentro do Dashboard em 29/09/2026: o Dashboard ficou só
      // com a operação. A tela é a mesma de antes, e a permissão é a que a consulta
      // dela exige no servidor.
      { label: 'Pendências', labelKey: 'nav.pending', path: '/logistica/pendencias', icon: TriangleAlert, ready: true, permission: 'pendencias.view' },
      { label: 'Mapa', labelKey: 'nav.map', path: '/logistica/mapa', icon: Map, ready: true, permission: 'map.view' },
      { label: 'Painel de Descarga', labelKey: 'nav.unloadingPanel', path: '/logistica/tickets', icon: ClipboardList, ready: true, permission: 'unloading.view' },
      { label: 'Frente de Trabalho', labelKey: 'nav.workFront', path: '/logistica/shipments', icon: Truck, ready: true, permission: 'shipments.view' },
      // Programação do cliente (Samuel, 03/11/2026): o que o cliente pediu pelo portal,
      // com o histórico, a carga extra e a redução, a geração das linhas na Frente e a
      // liberação das placas ao cliente.
      { label: 'Programação do cliente', labelKey: 'nav.customerSchedule', path: '/logistica/programacao-cliente', icon: CalendarClock, ready: true, matchNested: true, permission: 'contracts.programacao_ver' },
      { label: 'Transporte Máquina', labelKey: 'nav.machineTransport', path: '/logistica/transporte-maquina', icon: Tractor, ready: true, permission: 'machine_transport.view' },
      // Outros Serviços Transporte (Samuel, 01/10/2026): as viagens que não são de
      // espiga nem de prancha, que até aqui pediam uma Frente para uma viagem só.
      { label: 'Outros Serviços Transporte', labelKey: 'nav.otherTransportServices', path: '/logistica/outros-servicos-transporte', icon: Boxes, ready: true, permission: 'outros_servicos_transporte.view' },
      // Viagens com pastinha própria desde 30/09/2026: emprestava shipments.view, e a
      // gestão de frota não tinha como tratar alerta sem ganhar a Frente de Trabalho.
      { label: 'Viagens', labelKey: 'nav.trips', path: '/logistica/viagens', icon: Route, ready: true, permission: 'viagens.view' },
      // Notas Fiscais (Samuel, 28/10/2026): as notas de transporte que a Qive capturou
      // e o Protheus importou, ligadas à viagem. A permissão é a de Viagens, porque a
      // nota é documento da viagem.
      { label: 'Notas Fiscais', labelKey: 'nav.invoices', path: '/logistica/notas-fiscais', icon: ReceiptText, ready: true, permission: 'viagens.view' },
      // Pedágios da Edenred (14/10/2026): as passagens, o vínculo com a viagem e os eixos.
      { label: 'Pedágios', labelKey: 'nav.tolls', path: '/logistica/pedagios', icon: Ticket, ready: true, permission: 'pedagios.view' },
      // Colheita (Samuel, 05/10/2026): o Dashboard das cargas solicitadas e atendidas
      // da programação de colheita, e as medições que o Protheus lançou.
      { label: 'Colheita', labelKey: 'nav.harvest', path: '/logistica/colheita', icon: Wheat, ready: true, permission: 'colheita.view' },
      // Mensagens Teclado (Samuel, 10/09/2026): a macro que o motorista digita na
      // cabine, nota fiscal e veículo vazio, com placa, motorista, hora e mapa.
      { label: 'Mensagens Teclado', labelKey: 'nav.keyboardMessages', path: '/logistica/mensagens-teclado', icon: Keyboard, ready: true, permission: 'mensagem_teclado.view' },
      // Conjuntos: a tela de três abas (Caminhões, Máquinas, Utilitários). Fica
      // ativa em qualquer aba e no formulário de cada uma.
      { label: 'Conjuntos', labelKey: 'nav.sets', path: '/logistica/conjuntos', icon: Link2, ready: true, matchNested: true, permission: 'sets.view' },
      // Abastecimentos (16/10/2026): os abastecimentos lançados no Protheus e, em
      // outra aba, a Solicitação de abastecimento de 04/09/2026, o histórico das
      // solicitações geradas no Maquinário da frente, fora do grupo do WhatsApp.
      { label: 'Abastecimentos', labelKey: 'nav.fuelRequests', path: '/logistica/abastecimento', icon: Fuel, ready: true, permission: 'abastecimento.view' },
    ],
  },
  {
    title: 'Jornada',
    titleKey: 'nav.section.journey',
    items: [
      // Painel de Jornada: dashboard ao vivo da jornada ATS sobre a frota.
      // Permissão própria (journey.view): emprestar dashboard.view fazia a tela
      // sumir junto quando alguém desmarcava a pastinha Dashboard.
      { label: 'Painel de Jornada', labelKey: 'nav.journeyDashboard', path: '/logistica/jornada', icon: Activity, ready: true, permission: 'journey.view' },
      // Painel de Diárias: o lançamento do ERP e a decisão do HUB, lado a lado com a
      // jornada apurada. Vizinho da Jornada porque lê a mesma apuração, e com
      // permissão PRÓPRIA porque quem confere diária decide dinheiro.
      { label: 'Painel de Diárias', labelKey: 'nav.dailyAllowances', path: '/logistica/diarias', icon: CalendarCheck, ready: true, permission: 'diaria.view' },
      // Gestões, até 08/10/2026 chamado Advertências: o que a conferência de diária e
      // o relatório de infrações classificaram como Gestão, em três abas por etapa.
      { label: 'Gestões', labelKey: 'nav.warnings', path: '/logistica/advertencias', icon: FileWarning, ready: true, permission: 'advertencia.view' },
      // Hospedagem de operadores em hotéis (places categoria hotel). matchNested
      // mantém o item ativo no detalhe /logistica/hospedagem/:uuid.
      { label: 'Hospedagem', labelKey: 'nav.lodging', path: '/logistica/hospedagem', icon: Hotel, ready: true, matchNested: true, permission: 'lodging.view' },
      // Transferências de conjunto entre duplas: em Jornada desde 04/09/2026.
      // Transferências NÃO é leitura de conjunto: o servidor exige
      // unloading.launch e unloading.approve (routes/transferencias.py).
      { label: 'Transferências', labelKey: 'nav.transfers', path: '/logistica/conjuntos/transferencias', icon: ArrowLeftRight, ready: true, permission: 'unloading.launch' },
    ],
  },
  {
    title: 'Geral',
    titleKey: 'nav.section.general',
    items: [
      // Os cadastros da operação, num hub com uma aba por cadastro. Cada aba pede a
      // key do próprio módulo, então o item aparece para quem alcança qualquer uma.
      {
        label: 'Cadastros', labelKey: 'nav.masterData', path: '/logistica/cadastros', icon: Database, ready: true, matchNested: true,
        anyPermission: ['contracts.view', 'shipments.view', 'sets.view', 'master_data.view'],
      },
      // Os catálogos de tipo e classificação e os Status, de volta em 26/10/2026.
      { label: 'Categorias', labelKey: 'nav.categories', path: '/logistica/categorias', icon: Tags, ready: true, matchNested: true, permission: 'master_data.view' },
    ],
  },
]
